import React, { useCallback, useEffect } from "react";

import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import moment, { Moment } from "moment";

import { GET_DIGITAL_AGRONOMIST_PREFERENCES, UPDATE_DIGITAL_AGRONOMIST_PREFERENCES } from "consts";

import { MainCropOption } from "models";

import { Button, DatePicker, Flex, Form, KlimRadioGroup, Option, ProgressBar, Select, StickyFooterButtons } from "components";

import { EditPreferencesFormValues, FORM_ITEM_TYPE, OnboardingFormValues } from "pages/app/digital-agronomist/utils.tsx";

export default function OnboardingStepFormItem({
  fieldName,
  title,
  type,
  options,
  t,
  ctaButtonLabel,
  handleNextStep,
  mainCropsList,
  inputSelectLabel,
  year,
  subtitle,
  progressPercentage,
  showProgressBar = true,
  isOnboardingStep = true,
}: {
  fieldName: keyof OnboardingFormValues | keyof EditPreferencesFormValues;
  title: string;
  type: FORM_ITEM_TYPE;
  options: { label: string; value: boolean | string | number }[];
  t: (key: string) => string;
  ctaButtonLabel: string;
  handleNextStep: (fieldName: keyof OnboardingFormValues | keyof EditPreferencesFormValues, showNextTile?: boolean) => void;
  mainCropsList?: MainCropOption[];
  inputSelectLabel?: string;
  year?: string;
  subtitle?: string;
  progressPercentage?: number;
  showProgressBar?: boolean;
  isOnboardingStep?: boolean;
}) {
  const form = Form.useFormInstance();
  const initialValue = form.getFieldsValue(true);
  const displayTitle = year ? `${title} ${year}` : title;

  const isUpdatingPreferences = useIsMutating({ mutationKey: [UPDATE_DIGITAL_AGRONOMIST_PREFERENCES] });
  const isReFetchingPreferences = useIsFetching({ queryKey: [GET_DIGITAL_AGRONOMIST_PREFERENCES] });

  const getDefaultDatePickerOpenDate = (year: string, pickerName: "sowingDate" | "harvestDate"): Moment => {
    if (!year) return moment();
    const selectedCropId = form.getFieldValue([fieldName, "cropId"]);
    const formValue: Moment | undefined = form.getFieldValue([fieldName, pickerName]);
    const defaultValue = formValue || moment().year(parseInt(year)).month(moment().month()).day(moment().day());

    if (formValue || !selectedCropId || !mainCropsList || mainCropsList.length === 0) return defaultValue;

    const selectedMainCrop = mainCropsList.find((crop) => crop.id === selectedCropId);
    if (!selectedMainCrop) return defaultValue;

    const { calWeekHarvesting, calWeekSeeding, seasonId } = selectedMainCrop;
    const pickerYear = pickerName === "harvestDate" ? parseInt(year) : seasonId === 1 || seasonId === 3 ? parseInt(year) : parseInt(year) - 1;
    const pickerWeek = pickerName === "sowingDate" ? calWeekSeeding : calWeekHarvesting;
    const dateFromWeek = moment().year(pickerYear).week(pickerWeek).startOf("week");

    return dateFromWeek;
  };

  const validateFormField = useCallback(async () => {
    const isMainCropStep = type === FORM_ITEM_TYPE.MAIN_CROP;
    if (!isMainCropStep) return;

    const fieldValue = form.getFieldValue(fieldName);
    if (!fieldValue) return;

    const isFieldDirty = Object.values(fieldValue).length === 3 && Object.values(fieldValue).every((value) => value);
    if (!isFieldDirty) return;

    await form.validateFields([fieldName], { recursive: true, dirty: false });
  }, [fieldName, form, type]);

  useEffect(() => {
    validateFormField();
  }, [validateFormField]);

  return (
    <Flex className="onboarding-step-form-item-container" vertical={true} justify="center" align="flex-start" gap={"2rem"}>
      {showProgressBar ? <ProgressBar className="progress-bar" percent={progressPercentage || 0} showInfo={false} /> : null}
      <div>
        <h2>{displayTitle}</h2>
        {subtitle && <p className="margin-bottom-0">{subtitle}</p>}
      </div>
      {type === FORM_ITEM_TYPE.RADIO ? (
        <Form.Item name={fieldName} initialValue={initialValue[fieldName]} rules={[{ required: true, message: t("onboarding.errors.mandatoryField") }]}>
          <KlimRadioGroup options={options} vertical dataTestId={`digital-agronomist-onboarding-step-radio-group-${fieldName}`} />
        </Form.Item>
      ) : type === FORM_ITEM_TYPE.SELECT ? (
        <div style={{ width: "100%" }}>
          {/* @ts-expect-error need to convert to tsx */}
          <Select
            dataTestId={`digital-agronomist-onboarding-step-select-${fieldName}`}
            name={fieldName}
            label={inputSelectLabel}
            initialValue={initialValue[fieldName]}
            value={form.getFieldValue([fieldName])}
            rules={[{ required: true, message: t("onboarding.errors.mandatoryField") }]}
            onChange={() => {
              // for the main crops number selector, we want the change to be immediately reflected to the form, this way we are sure that the value is always
              // coherent and there are no issues with the form validation at the end
              if (fieldName === "mainCropsNumber") {
                handleNextStep(fieldName, false);
              }
            }}
          >
            {options.map((option, i) => (
              <Option
                key={option.value.toString()}
                data-testid={`digital-agronomist-onboarding-step-select-${fieldName}-option-${i}`}
                value={option.value}
                label={option.label}
              >
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
      ) : type === FORM_ITEM_TYPE.MAIN_CROP ? (
        <Flex vertical gap="2rem" style={{ width: "100%" }}>
          <Flex vertical gap="1rem">
            <h5>{`${t("onboarding.steps.mainCropDetails.mainCrop")} ${year}`}</h5>
            {/* @ts-expect-error need to convert to tsx */}
            <Select
              dataTestId={`digital-agronomist-onboarding-step-select-${fieldName}`}
              name={[fieldName, "cropId"]}
              label={inputSelectLabel}
              initialValue={initialValue?.cropId}
              value={form.getFieldValue([fieldName, "cropId"])}
              rules={[{ required: true, message: t("onboarding.errors.mandatoryField") }]}
              filterOption={(inputValue: string, option: { value: string; label: string }) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
              showSearch
              onChange={() => {
                // We need to reset the sowing and harvest dates form values when the cropId changes to refresh the defaultValue
                const sowingDate = form.getFieldValue([fieldName, "sowingDate"]);
                if (!sowingDate) form.resetFields([[fieldName, "sowingDate"]]);

                const harvestDate = form.getFieldValue([fieldName, "harvestDate"]);
                if (!harvestDate) form.resetFields([[fieldName, "harvestDate"]]);
              }}
            >
              {options?.map((option, i) => (
                <Option
                  key={option.value as string}
                  data-testid={`digital-agronomist-onboarding-step-select-${fieldName}-option-${i}`}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Option>
              ))}
            </Select>
          </Flex>
          <Flex justify="space-between" align="center" gap="1rem">
            <Flex vertical gap="1rem" className="date-picker-container">
              <h5>{t("onboarding.steps.mainCropDetails.sowingDate")}</h5>
              <DatePicker
                key={form.getFieldValue([fieldName, "cropId"])}
                dataTestId={`digital-agronomist-onboarding-step-sowing-date-${fieldName}`}
                name={[fieldName, "sowingDate"]}
                label={t("onboarding.steps.mainCropDetails.date")}
                initialValue={initialValue?.sowingDate}
                value={form.getFieldValue([fieldName, "sowingDate"])}
                rules={[
                  { required: true, message: t("onboarding.errors.mandatoryField") },
                  {
                    validator: (_: unknown, sowingDate: Dayjs) => {
                      if (!sowingDate) return Promise.resolve();
                      const harvestDate: Dayjs | undefined = form.getFieldValue([fieldName, "harvestDate"]);
                      if (!harvestDate) return Promise.resolve();

                      if (harvestDate.isBefore(sowingDate, "day") || harvestDate.isSame(sowingDate, "day")) {
                        return Promise.reject(t("onboarding.errors.sowingDateAfterHarvestDate"));
                      }
                      return Promise.resolve();
                    },
                    message: t("onboarding.errors.sowingDateAfterHarvestDate"),
                  },
                ]}
                format={["DD.MM.YYYY"]}
                disabledDate={(date: Moment) => checkDateValidity(date, `${parseInt(year as string) - 1}`, year as string)}
                defaultPickerValue={getDefaultDatePickerOpenDate(year as string, "sowingDate")}
              />
            </Flex>
            <div className="date-picker-separator" />
            <Flex vertical gap="1rem" className="date-picker-container">
              <h5>{t("onboarding.steps.mainCropDetails.harvestDate")}</h5>
              <DatePicker
                key={form.getFieldValue([fieldName, "cropId"])}
                dataTestId={`digital-agronomist-onboarding-step-harvest-date-${fieldName}`}
                name={[fieldName, "harvestDate"]}
                label={t("onboarding.steps.mainCropDetails.date")}
                initialValue={initialValue?.harvestDate}
                value={form.getFieldValue([fieldName, "harvestDate"])}
                rules={[{ required: true, message: t("onboarding.errors.mandatoryField") }]}
                format={["DD.MM.YYYY"]}
                disabledDate={(date: Moment) => checkDateValidity(date, year as string, year as string)}
                defaultPickerValue={getDefaultDatePickerOpenDate(year as string, "harvestDate")}
              />
            </Flex>
          </Flex>
        </Flex>
      ) : null}
      {isOnboardingStep ? (
        <Button
          type="primary"
          className="margin-top-50"
          dataTestId="onboarding-step-form-item-cta-button"
          onClick={(e) => {
            handleNextStep(fieldName);

            // remove focus from the button after the function runs to remove the css :focus styling
            if (e.target instanceof HTMLButtonElement) e.target.blur();
          }}
        >
          {ctaButtonLabel}
        </Button>
      ) : (
        <StickyFooterButtons className="digital-agronomist-sticky-footer-button">
          <Button
            type="primary"
            className="margin-top-50"
            dataTestId="onboarding-step-form-item-cta-button"
            loading={isUpdatingPreferences + isReFetchingPreferences !== 0}
            onClick={(e) => {
              handleNextStep(fieldName);

              // remove focus from the button after the function runs to remove the css :focus styling
              if (e.target instanceof HTMLButtonElement) e.target.blur();
            }}
          >
            {ctaButtonLabel}
          </Button>
        </StickyFooterButtons>
      )}
    </Flex>
  );
}

export const checkDateValidity = (date: Moment, startYear: string, endYear: string) => {
  if (!date) return true;

  return !moment(date).isBetween(moment(`01/01/${parseInt(startYear)}`, "DD.MM.YYYY"), moment(`01/01/${parseInt(endYear) + 1}`, "DD.MM.YYYY"));
};
